import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LayoutDashboard, Ticket, AlertCircle, Clock, CheckCircle2, XCircle,
  Loader2, Users, LogOut, Shield, TrendingUp, BarChart3, Filter, Building2,
  ArrowUpRight, ArrowDownRight, Minus, Settings, CalendarDays
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from 'recharts';
import CsvExportButton from '@/components/CsvExportButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

// ── Helpers ─────────────────────────────────────────────────────────────────

const toLocalYYYYMMDD = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d)) return '';
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const fmt = d => toLocalYYYYMMDD(d);

const buildPreset = (val) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (val === 'all') return { start: '', end: '' };
  if (val === 'today') return { start: fmt(today), end: fmt(today) };
  if (val === 'last_7') { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: fmt(s), end: fmt(today) }; }
  if (val === 'this_week') { const d = new Date(today); const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); return { start: fmt(d), end: fmt(today) }; }
  if (val === 'this_month') return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
  if (val === 'last_month') { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { start: fmt(s), end: fmt(e) }; }
  if (val === 'ytd') return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today) };
  if (val === 'last_year') return { start: fmt(new Date(today.getFullYear() - 1, 0, 1)), end: fmt(new Date(today.getFullYear() - 1, 11, 31)) };
  return { start: '', end: '' };
};

const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', resolved: '#22c55e', closed: '#9ca3af' };
const PRIORITY_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
const CHART_COLORS = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

const StatCard = ({ title, value, sub, icon: Icon, color = 'gray', trend }) => {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  const cls = colorMap[color] || colorMap.gray;
  return (
    <Card className={`border ${cls}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center opacity-20`}
            style={{ background: 'currentColor' }}>
          </div>
        </div>
        {Icon && <Icon className="w-5 h-5 mt-2 opacity-40" />}
      </CardContent>
    </Card>
  );
};

// Simple horizontal bar row
const BarRow = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-24 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-semibold text-gray-800 w-6 text-right">{value}</span>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('this_month');
  const [dateRange, setDateRange] = useState(buildPreset('this_month'));

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ticketsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, getAuthHeaders()),
        axios.get(`${API}/tickets`, getAuthHeaders()),
        axios.get(`${API}/companies`, getAuthHeaders()),
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
      
      const compMap = {};
      companiesRes.data.forEach(c => { compMap[c.id] = c.name; });
      setCompanies(compMap);
    } catch { toast.error('Failed to load dashboard data'); }
    finally { setLoading(false); }
  };

  const handlePreset = (val) => {
    setPreset(val);
    setDateRange(buildPreset(val));
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const filtered = tickets.filter(t => {
    if (!dateRange.start && !dateRange.end) return true;
    const d = toLocalYYYYMMDD(t.due_date);
    if (!d) return false;
    if (dateRange.start && d < dateRange.start) return false;
    if (dateRange.end && d > dateRange.end) return false;
    return true;
  });

  const byStatus = {
    open: filtered.filter(t => t.status === 'open').length,
    in_progress: filtered.filter(t => t.status === 'in_progress').length,
    resolved: filtered.filter(t => t.status === 'resolved').length,
    closed: filtered.filter(t => t.status === 'closed').length,
  };
  const byPriority = {
    critical: filtered.filter(t => t.priority === 'critical').length,
    high: filtered.filter(t => t.priority === 'high').length,
    medium: filtered.filter(t => t.priority === 'medium').length,
    low: filtered.filter(t => t.priority === 'low').length,
  };

  const totalHours = filtered.reduce((s, t) => s + (t.total_hours_spent || 0), 0);
  const resolvedRate = filtered.length > 0 ? Math.round(((byStatus.resolved + byStatus.closed) / filtered.length) * 100) : 0;
  const criticalOpen = filtered.filter(t => t.priority === 'critical' && t.status === 'open').length;

  // Tickets by day (last 14 days in range)
  const ticketsByDay = (() => {
    const map = {};
    filtered.forEach(t => {
      const d = toLocalYYYYMMDD(t.due_date);
      if (d) map[d] = (map[d] || 0) + 1;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).slice(-14).map(([date, count]) => ({
      date: new Date(date + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      tickets: count,
    }));
  })();

  // Top clients by ticket count
  const topByCompany = (() => {
    const map = {};
    filtered.forEach(t => { 
      const name = companies[t.company_id];
      if (!name) return; // skip if no valid company
      map[name] = (map[name] || 0) + 1; 
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 6).map(([name, count]) => ({ name, count }));
  })();

  // Hours by problem type
  const hoursByType = (() => {
    const map = {};
    filtered.forEach(t => {
      const types = (t.problem_type || 'other').split(',').map(s => s.trim()).filter(Boolean);
      types.forEach(k => {
        map[k] = (map[k] || 0) + (t.total_hours_spent || 0);
      });
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 6).map(([type, hours]) => ({
      type: type.replace(/_/g, ' '),
      hours: parseFloat(hours.toFixed(1)),
    }));
  })();

  // Tickets by Requester (account executive / employee)
  const ticketsByRequester = (() => {
    const map = {};
    filtered.forEach(t => {
      const name = t.requested_by_name;
      if (name) map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 8).map(([name, count]) => ({ name, count }));
  })();

  const pieData = [
    { name: 'Open', value: byStatus.open, color: STATUS_COLORS.open },
    { name: 'In Progress', value: byStatus.in_progress, color: STATUS_COLORS.in_progress },
    { name: 'Resolved', value: byStatus.resolved, color: STATUS_COLORS.resolved },
    { name: 'Closed', value: byStatus.closed, color: STATUS_COLORS.closed },
  ].filter(d => d.value > 0);

  const priorityPieData = [
    { name: 'Critical', value: byPriority.critical, color: PRIORITY_COLORS.critical },
    { name: 'High', value: byPriority.high, color: PRIORITY_COLORS.high },
    { name: 'Medium', value: byPriority.medium, color: PRIORITY_COLORS.medium },
    { name: 'Low', value: byPriority.low, color: PRIORITY_COLORS.low },
  ].filter(d => d.value > 0);

  const PRESETS = [
    ['today', 'Today'], ['last_7', 'Last 7d'], ['this_week', 'This Week'],
    ['this_month', 'This Month'], ['last_month', 'Last Month'], ['ytd', 'Year to Date'], ['all', 'All Time'],
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">Overview & Analytics</p>
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-gray-900">Management workspace</p>
              <p className="text-xs text-gray-500">Use the sidebar to navigate</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Time Range Filter */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-8 flex flex-wrap gap-2 items-center">
              <Filter className="w-4 h-4 text-gray-400 mr-1" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">Period:</span>
              {PRESETS.map(([val, label]) => (
                <button key={val} onClick={() => handlePreset(val)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${preset === val ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {label}
                </button>
              ))}
              <span className="text-gray-200 mx-1">|</span>
              <input type="date" value={dateRange.start}
                onChange={e => { setPreset('custom'); setDateRange(r => ({ ...r, start: e.target.value })); }}
                className="text-xs border border-gray-200 rounded px-2 py-1 h-7" />
              <span className="text-gray-400 text-xs">to</span>
              <input type="date" value={dateRange.end}
                onChange={e => { setPreset('custom'); setDateRange(r => ({ ...r, end: e.target.value })); }}
                className="text-xs border border-gray-200 rounded px-2 py-1 h-7" />
              <div className="ml-auto flex items-center gap-3">
                <span className="text-xs text-gray-400">{filtered.length} tickets in range</span>
                <CsvExportButton
                  filename="admin-dashboard-tickets"
                  rows={filtered}
                  columns={[
                    { label: 'Ticket ID', value: 'id' },
                    { label: 'Title', value: 'title' },
                    { label: 'Company', value: row => row.company_name || row.company_id || '' },
                    { label: 'Status', value: 'status' },
                    { label: 'Priority', value: 'priority' },
                    { label: 'Due Date', value: 'due_date' },
                    { label: 'Hours', value: 'total_hours_spent' },
                    { label: 'Created By', value: 'created_by_email' },
                  ]}
                />
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard title="Total Tickets" value={filtered.length} sub="in selected period" icon={Ticket} color="gray" />
              <StatCard title="Open" value={byStatus.open} sub="needs attention" icon={Clock} color="blue" />
              <StatCard title="In Progress" value={byStatus.in_progress} sub="being worked on" icon={Loader2} color="yellow" />
              <StatCard title="Resolved" value={byStatus.resolved + byStatus.closed} sub={`${resolvedRate}% resolution rate`} icon={CheckCircle2} color="green" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard title="Critical Open" value={criticalOpen} sub="urgent attention" icon={AlertCircle} color="red" />
              <StatCard title="High Priority" value={byPriority.high} icon={TrendingUp} color="orange" />
              <StatCard title="Hours Logged" value={`${totalHours.toFixed(1)}h`} sub="total work time" icon={Clock} color="indigo" />
              <StatCard title="Total Users" value={stats?.total_users || 0} sub={`${stats?.support_clients || 0} support clients`} icon={Users} color="purple" />
            </div>

            {/* Charts Row 1: Tickets Over Time + Status Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Ticket Volume Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  {ticketsByDay.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data in range</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={ticketsByDay} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="tGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Area type="monotone" dataKey="tickets" stroke="#6366f1" fill="url(#tGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={68} paddingAngle={3}>
                            {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [v, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-3 space-y-1.5">
                        {pieData.map(d => (
                          <BarRow key={d.name} label={d.name} value={d.value} max={filtered.length} color={d.color} />
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2: Priority + Top Users + Hours by Type */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Priority Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {priorityPieData.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={priorityPieData} dataKey="value" cx="50%" cy="50%" outerRadius={68} paddingAngle={3}>
                            {priorityPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-3 space-y-1.5">
                        {priorityPieData.map(d => (
                          <BarRow key={d.name} label={d.name} value={d.value} max={filtered.length} color={d.color} />
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Top Companies by Tickets</CardTitle>
                </CardHeader>
                <CardContent>
                  {topByCompany.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={topByCompany} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Hours by Problem Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {hoursByType.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No time logged</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={hoursByType} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="type" tick={{ fontSize: 10 }} width={95} />
                        <Tooltip />
                        <Bar dataKey="hours" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Tickets by Requester */}
            {ticketsByRequester.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Tickets by Requester</CardTitle>
                  <p className="text-xs text-gray-500">Account executives / employees who submitted requests</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={Math.max(160, ticketsByRequester.length * 36)}>
                    <BarChart data={ticketsByRequester} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Resolution funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Ticket Pipeline — Status Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: 'Open', count: byStatus.open, color: '#3b82f6', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
                    { label: 'In Progress', count: byStatus.in_progress, color: '#f59e0b', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
                    { label: 'Resolved', count: byStatus.resolved, color: '#22c55e', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
                    { label: 'Closed', count: byStatus.closed, color: '#9ca3af', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' },
                  ].map((s, i) => (
                    <div key={s.label} className={`${s.bg} ${s.border} border rounded-xl p-4 text-center relative`}>
                      {i > 0 && (
                        <div className="absolute -left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg font-light select-none">›</div>
                      )}
                      <p className={`text-xs font-semibold uppercase tracking-wide ${s.text} mb-1`}>{s.label}</p>
                      <p className={`text-3xl font-bold ${s.text}`}>{s.count}</p>
                      <p className="text-xs text-gray-400 mt-1">{filtered.length > 0 ? Math.round((s.count / filtered.length) * 100) : 0}%</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
